import React from 'react';
import { Row, Col, Card, ProgressBar } from 'react-bootstrap';
import type { FeedbackAnalyticsDto } from '../../../../services/feedback-analytics';
import InfoHint from './InfoHint';

interface OverviewCardsProps {
  data: FeedbackAnalyticsDto | null;
  loading?: boolean;
}

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: string;
  color: string;
  subtitle?: string;
}> = ({ title, value, icon, color, subtitle }) => (
  <Card className="shadow-sm border-0 h-100" style={{ borderLeft: `4px solid ${color}` }}>
    <Card.Body>
      <div className="d-flex align-items-start">
        <div className="flex-grow-1">
          <small className="text-muted d-block mb-2">{title}</small>
          <h4 className="mb-0" style={{ color }}>
            {value}
          </h4>
          {subtitle && <small className="text-muted d-block mt-2">{subtitle}</small>}
        </div>
        <div className="ms-3 text-center" style={{ fontSize: '32px', opacity: 0.3 }}>
          {icon}
        </div>
      </div>
    </Card.Body>
  </Card>
);

export const OverviewCards: React.FC<OverviewCardsProps> = ({
  data,
  loading = false,
}) => {
  if (!data) {
    return (
      <div className="alert alert-info text-center my-4">
        Nenhum dado disponível
      </div>
    );
  }

  const topForm = data.topRatedForms[0];
  const bottomForm = data.lowestRatedForms[0];
  const npsColor =
    data.npsSummary.npsScore >= 50
      ? '#198754'
      : data.npsSummary.npsScore >= 0
        ? '#fd7e14'
        : '#dc3545';

  return (
    <Row className="g-4 mb-5">
      {/* Card: Total de Reviews */}
      <Col xs={12} md={6} lg={3}>
        <StatCard
          title="Total de Reviews"
          value={data.totalReviews}
          icon="📄"
          color="#0d6efd"
        />
      </Col>

      {/* Card: Avaliação Média */}
      <Col xs={12} md={6} lg={3}>
        <Card className="shadow-sm border-0 h-100" style={{ borderLeft: '4px solid #ffc107' }}>
          <Card.Body>
            <small className="text-muted d-flex align-items-center mb-2">
              Avaliação Média
              <InfoHint
                id="avg-rating"
                text="Média das notas de 0 a 10 extraídas dos feedbacks no período selecionado."
              />
            </small>
            <h4 className="mb-3" style={{ color: '#ffc107' }}>
              {data.averageRating.toFixed(1)} / 10
            </h4>
            <ProgressBar
              now={Math.round((data.averageRating / 10) * 100)}
              variant="warning"
              style={{ height: '6px' }}
            />
          </Card.Body>
        </Card>
      </Col>

      {/* Card: NPS */}
      <Col xs={12} md={6} lg={3}>
        <Card className="shadow-sm border-0 h-100" style={{ borderLeft: `4px solid ${npsColor}` }}>
          <Card.Body>
            <small className="text-muted d-flex align-items-center mb-2">
              NPS
              <InfoHint
                id="nps-score"
                text="Net Promoter Score: porcentagem de Promotores (9-10) menos porcentagem de Detratores (0-6)."
              />
            </small>
            <h4 className="mb-1" style={{ color: npsColor }}>
              {data.npsSummary.npsScore}
            </h4>
            <small className="text-muted d-block">
              Promotores {data.npsSummary.promotersPercentage}% | Neutros {data.npsSummary.passivesPercentage}% | Detratores {data.npsSummary.detractorsPercentage}%
            </small>
          </Card.Body>
        </Card>
      </Col>

      {/* Card: Melhor Formulário */}
      <Col xs={12} md={6} lg={3}>
        <Card className="shadow-sm border-0 h-100" style={{ borderLeft: '4px solid #28a745' }}>
          <Card.Body>
            <small className="text-muted d-flex align-items-center mb-2">
              Melhor Formulário
              <InfoHint
                id="best-form"
                text="Formulário com maior avaliação média no período e filtros selecionados."
              />
            </small>
            <div className="font-semibold text-dark mb-2 text-truncate">
              {topForm?.formTitle || 'N/A'}
            </div>
            <h5 style={{ color: '#28a745' }} className="mb-1">
              {(topForm?.averageRating || 0).toFixed(1)} / 10
            </h5>
            <small className="text-muted d-block">
              {topForm?.totalReviews || 0} avaliações
            </small>
          </Card.Body>
        </Card>
      </Col>

      {/* Card: Formulário com Menor Avaliação */}
      <Col xs={12} md={6} lg={3}>
        <Card className="shadow-sm border-0 h-100" style={{ borderLeft: '4px solid #dc3545' }}>
          <Card.Body>
            <small className="text-muted d-flex align-items-center mb-2">
              Requer Atenção
              <InfoHint
                id="needs-attention"
                text="Formulário com menor avaliação média no período e filtros selecionados."
              />
            </small>
            <div className="font-semibold text-dark mb-2 text-truncate">
              {bottomForm?.formTitle || 'N/A'}
            </div>
            <h5 style={{ color: '#dc3545' }} className="mb-1">
              {(bottomForm?.averageRating || 0).toFixed(1)} / 10
            </h5>
            <small className="text-muted d-block">
              {bottomForm?.totalReviews || 0} avaliações
            </small>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default OverviewCards;
